import {OnBehalfOf} from "@/shared/composable/security.ts";
import {
    Basket,
    BasketInput,
    BasketPromotionsInput,
    BasketResponse,
    BasketValidation,
    CheckoutConfigInput,
    CreateCheckoutResponse,
    InvalidVariantLineItem,
    LineItem,
    LineItemInput,
    ManualDiscountInput,
    PromoLineItem,
    Promotion,
    PromotionResponse,
    VariantIdentifier,
    VariantIdentifierInput,
    VariantLineItem
} from "@/shared/graphql/types";
import {ClientBasket} from "@/shared/services/basket/ClientBasket.ts";
// @ts-ignore
import {BasketService} from "@/shared/services/basket/basket.service.ts";
import {getApolloClient} from "@/shared/apolloclient/apollo.client.ts";
// @ts-ignore
import UPDATE_BASKET_MUTATION from "@/shared/graphql/basket/basket.mutation.graphql";
// @ts-ignore
import APPLY_PROMOTION_MUTATION from "@/shared/graphql/basket/basket.promo.mutation.graphql";
// @ts-ignore
import CHECKOUT_BASKET_MUTATION from "@/shared/graphql/basket/basket.checkout.mutation.graphql";
// @ts-ignore
import LOAD_BASKET_QUERY from "@/shared/graphql/basket/basket.query.graphql";
// @ts-ignored
import LOAD_BASKETS_QUERY from "@/shared/graphql/basket/baskets.query.graphql";
import type {DocumentNode} from "graphql/index";
import type {TypedDocumentNode} from "@graphql-typed-document-node/core";
import type {OperationVariables} from "@apollo/client/core/types";
import {extractGraphData} from "@/shared/composable/service.composables.ts";
import {ClientLineItem, ClientLineItemType} from "@/shared/services/basket/ClientLineItem.ts";
import {activeLanguage} from "@/shared/i18n/i18n.ts";

export interface BasketService {

    loadBasket(contactId: string, onBehalfOf?: OnBehalfOf | null): Promise<ClientBasket>;

    loadBaskets(customerId: string, onBehalfOf?: OnBehalfOf | null): Promise<ClientBasket[]>;

    loadGuestBasket(guestId: string): Promise<ClientBasket>;

    updateBasket(basket: ClientBasket): Promise<ClientBasket>;

    applyPromotion(basket: ClientBasket, promotion: Promotion): Promise<ClientBasket>;

    deletePromotion(basket: ClientBasket, promotion: Promotion): Promise<ClientBasket>;

    checkout(config: CheckoutConfigInput, basket: ClientBasket): Promise<CreateCheckoutResponse>
}

class BasketServerService implements BasketService {

    async loadBasket(contactId: string, onBehalfOf?: OnBehalfOf | null) {
        const response = (await this.executeBasketQuery(LOAD_BASKET_QUERY, {
            language: activeLanguage(),
            contact: contactId,
            guestId: null
        }, onBehalfOf))
        const basket = new ClientBasket(response.basket.id, response.basket.etag, response.validation)
        basket.onBehalfOf = onBehalfOf
        return this.toClientBasket(response.basket, response.validation, basket)
    }

    async loadBaskets(customerId: string, onBehalfOf?: OnBehalfOf | null): Promise<ClientBasket[]> {
        const response = await getApolloClient().query({
            query: LOAD_BASKETS_QUERY,
            variables: {
                language: activeLanguage(),
                customerId: customerId
            },
            fetchPolicy: "no-cache",
            context: {"OnBehalfOf": onBehalfOf ?? (customerId ? new OnBehalfOf(Number(customerId)) : null)}
        });
        const baskets = response?.data.tenant.baskets
        return baskets.map((basketResponse: BasketResponse) => {
            const basket = new ClientBasket(basketResponse.basket.id, basketResponse.basket.etag, basketResponse.validation)
            basket.onBehalfOf = new OnBehalfOf(Number(customerId), basketResponse.basket.customer.contact ? Number(basketResponse.basket.customer.contact.id) : null)
            return this.toClientBasket(basketResponse.basket, basketResponse.validation, basket)
        })
    }

    async updateBasket(basket: ClientBasket) {
        const input = {
            items: this.toBasketInputs(basket.items),
            promotions: basket.promotionsInput
        } as BasketInput
        const response = await (this.executeBasketMutation<BasketResponse>(UPDATE_BASKET_MUTATION, {
            language: activeLanguage(),
            guestId: basket.guestId,
            input: input
        }, basket.onBehalfOf))
        return this.toClientBasket(response.basket, response.validation, basket)
    }

    async applyPromotion(basket: ClientBasket, promotion: Promotion) {
        const response = await (this.executeBasketMutation<BasketResponse>(APPLY_PROMOTION_MUTATION, {
            language: activeLanguage(),
            guestId: basket.guestId,
            id: promotion.id
        }, basket.onBehalfOf))
        return this.toClientBasket(response.basket, response.validation, basket)
    }

    async deletePromotion(basket: ClientBasket, promotion: Promotion): Promise<ClientBasket> {
        const updatedApplied = basket.promotionsInput.applied.filter((id) => id !== promotion.id);
        const updatedRejected = Array.from(new Set([...basket.promotionsInput.rejected, promotion.id]));
        const input = {
            items: this.toBasketInputs(basket.items.filter(item => item.promotion?.id !== promotion.id)),
            promotions: {
                applied: updatedApplied,
                rejected: updatedRejected,
            }
        } as BasketInput
        const response = await (this.executeBasketMutation<BasketResponse>(UPDATE_BASKET_MUTATION, {
            language: activeLanguage(),
            guestId: basket.guestId,
            input: input
        }, basket.onBehalfOf))
        return this.toClientBasket(response.basket, response.validation, basket)
    }


    async checkout(config: CheckoutConfigInput, basket: ClientBasket): Promise<CreateCheckoutResponse> {
        return await (this.executeBasketMutation<CreateCheckoutResponse>(CHECKOUT_BASKET_MUTATION, {
            language: activeLanguage(),
            guestId: basket.guestId,
            config: config
        }, basket.onBehalfOf))
    }

    async loadGuestBasket(guestId: string): Promise<ClientBasket> {
        const response = (await this.executeBasketQuery(LOAD_BASKET_QUERY, {
            language: activeLanguage(),
            guestId: guestId
        }))
        const basket = new ClientBasket(response.basket.id, response.basket.etag, response.validation)
        basket.guestId = guestId
        return this.toClientBasket(response.basket, response.validation, basket)
    }

    private executeBasketQuery = async (query: DocumentNode | TypedDocumentNode, variables: OperationVariables, onBehalfOf?: OnBehalfOf | null): Promise<BasketResponse> => {
        const response = await getApolloClient().query({
            query: query,
            variables: {
                language: activeLanguage(),
                ...variables,
            },
            fetchPolicy: "no-cache",
            context: {"OnBehalfOf": onBehalfOf}
        });
        return extractGraphData(response?.data)
    }
    private executeBasketMutation = async <T>(mutation: DocumentNode | TypedDocumentNode, variables: OperationVariables, onBehalfOf?: OnBehalfOf | null): Promise<T> => {
        const response = await getApolloClient().mutate({
            mutation: mutation,
            variables: {
                language: activeLanguage(),
                ...variables,
            },
            context: {"OnBehalfOf": onBehalfOf}
        });
        return extractGraphData<T>(response?.data)
    }

    private toBasketInputs(items: ClientLineItem[]): LineItemInput[] {
        return items.filter((item) => item.type === "VARIANT" || item.promotion?.quantityLinked == false).map((item) => {
            return {
                id: item.id,
                identifier: {kind: item.identifier.kind, value: item.identifier.value} as VariantIdentifierInput,
                quantity: item.quantity,
                remark: item.remark,
                unit: item.unit.id,
                discount: item.discount
            } as LineItemInput
        })
    }

    private toClientBasket(basket: Basket, validation: BasketValidation, b: ClientBasket): ClientBasket {
        const clientBasket = new ClientBasket(basket.id, basket.etag, validation)
        clientBasket.displayVatTotals = basket.displayVatTotals
        clientBasket.paysVat = basket.paysVat
        clientBasket.onBehalfOf = b.onBehalfOf
        clientBasket.contact = basket.customer.contact
        // clientBasket.guestId = basket.customer.contact ? null : basket.id
        clientBasket.totalItemQuantity = basket.totalItemQuantity
        clientBasket.cost = basket.cost
        clientBasket.promotions = basket.promotions
        clientBasket.isRepresentativeBasket = basket.isRepresentativeBasket
        clientBasket.contact = basket.isRepresentativeBasket ? null : basket.customer.contact
        clientBasket.items = this.toClientLineItems(basket.items)
        clientBasket.promotionsInput = this.toPromotionsInputs(basket.promotions)
        return clientBasket
    }

    private toClientLineItems(items: LineItem[]): ClientLineItem[] {
        return items.map((item: LineItem) => {
            if (item['__typename'] === 'VariantLineItemImpl') {
                const manualDiscount = (item as VariantLineItem).discount ? {
                    amount: (item as VariantLineItem).discount.amount,
                    type: (item as VariantLineItem).discount.type
                } as ManualDiscountInput : null;
                const lineItem = new ClientLineItem(item.id,
                    (item as VariantLineItem).identifier,
                    ClientLineItemType.Variant,
                    (item as VariantLineItem).variant.type,
                    item['variant']?.code?.value ?? item['invalidVariant']?.code?.value,
                    item['variant']?.name?.actual ?? item['invalidVariant']?.name?.actual,
                    item.unit,
                    item.quantity,
                    item.unitQuantity,
                    1,
                    item.remark,
                    item.image ?? item['invalidVariant']?.featuredImage,
                    item.unitPrice,
                    item.totalPrice,
                    item.errors,
                    manualDiscount,
                    (item as VariantLineItem).discountItems
                )
                lineItem.includedItems = (item as VariantLineItem).includedItems
                lineItem.extraItems = (item as VariantLineItem).extraItems
                lineItem.comparePrice = (item as VariantLineItem).comparePrice
                return lineItem
            } else if (item['__typename'] === 'PromoLineItem') {
                let promoItem = (item as PromoLineItem)
                let lineItem = new ClientLineItem(promoItem.id,
                    {kind: 'ID', value: promoItem['variant']?.id} as VariantIdentifier,
                    ClientLineItemType.Promotion,
                    promoItem.variant.type,
                    promoItem['variant']?.code?.value ?? promoItem['invalidVariant']?.code?.value,
                    promoItem['variant']?.name?.actual ?? promoItem['invalidVariant']?.name?.actual,
                    promoItem.unit,
                    promoItem.quantity,
                    promoItem.unitQuantity,
                    promoItem.stepSize,
                    promoItem.remark,
                    //@ts-ignore
                    promoItem.image,
                    promoItem.unitPrice,
                    promoItem.totalPrice,
                    [],
                    null,
                    []
                )

                lineItem.promotion = promoItem.promotion
                if (promoItem.linesIds.length == 1) {
                    lineItem.parentLineItem = promoItem.linesIds[0]
                }

                return lineItem
            } else if (item['__typename'] === 'InvalidVariantLineItem') {
                let invalidItem = (item as InvalidVariantLineItem)
                return new ClientLineItem(invalidItem.id,
                    {kind: 'ID', value: invalidItem['variant']?.id} as VariantIdentifier,
                    ClientLineItemType.Invalid,
                    invalidItem['variant']?.type ?? invalidItem['invalidVariant']?.type,
                    invalidItem['variant']?.code?.value ?? invalidItem['invalidVariant']?.code?.value,
                    invalidItem['variant']?.name?.actual ?? invalidItem['invalidVariant']?.name?.actual,
                    invalidItem.unit,
                    invalidItem.quantity,
                    invalidItem.unitQuantity,
                    1,
                    invalidItem.remark,
                    //@ts-ignore
                    invalidItem.image,
                    invalidItem.unitPrice,
                    invalidItem.totalPrice,
                    invalidItem.errors,
                    null,
                    []
                )
            } else throw new TypeError("Unknown Line Item Type")
        }).filter(item => item != null)
    }

    private toPromotionsInputs(promotions: PromotionResponse): BasketPromotionsInput {
        const applied = promotions.applied.map((p: Promotion) => p.id)
        const rejected = promotions.rejected.map((p: Promotion) => p.id)
        return {
            applied: applied,
            rejected: rejected
        } as BasketPromotionsInput
    }
}

const basketService = new BasketServerService();
export default basketService